import {
  generateCharacterAppearance,
  type BodyShape,
  type CarriedAccessory,
  type CharacterAgeGroup,
  type CharacterAppearance,
  type CharacterAppearanceOverrides,
  type CharacterStature,
  type Eyewear,
  type FacialHair,
  type Footwear,
  type HairColour,
  type HairStyle,
  type Headwear,
  type LowerGarment,
  type Neckwear,
  type Outerwear,
} from "./appearance.ts";

export const RESIDENT_ARCHETYPE_OPTIONS = Object.freeze([
  { id: "everyday_local", label: "Everyday local" },
  { id: "sportswear_local", label: "Sportswear local" },
  { id: "senjore", label: "Senjorė" },
  { id: "active_senior", label: "Active senior" },
  { id: "senior_gentleman", label: "Senior gentleman" },
  { id: "business_professional", label: "Business professional" },
  { id: "civic_official", label: "Civic official" },
  { id: "student", label: "Student" },
  { id: "young_local", label: "Young local" },
  { id: "creative_local", label: "Creative local" },
  { id: "service_worker", label: "Service worker" },
  { id: "practical_worker", label: "Practical worker" },
  { id: "commuter", label: "Commuter" },
  { id: "tech_worker", label: "Technology worker" },
  { id: "university_researcher", label: "University researcher" },
  { id: "bike_commuter", label: "Bicycle commuter" },
  { id: "cafe_worker", label: "Café worker" },
  { id: "market_vendor", label: "Market vendor" },
  { id: "health_worker", label: "Health worker" },
  { id: "park_worker", label: "Park worker" },
  { id: "caregiver", label: "Caregiver" },
  { id: "city_visitor", label: "City visitor" },
  { id: "tour_guide", label: "Tour guide" },
  { id: "old_town_craftsperson", label: "Old Town craftsperson" },
  { id: "restoration_worker", label: "Restoration worker" },
  { id: "dog_walker", label: "Dog walker" },
  { id: "recreational_runner", label: "Recreational runner" },
] as const);

export type ResidentArchetypeId =
  typeof RESIDENT_ARCHETYPE_OPTIONS[number]["id"];

export const RESIDENT_ARCHETYPE_CONTEXTS = Object.freeze({
  citywide: Object.freeze([
    "everyday_local", "sportswear_local", "senjore", "active_senior",
    "senior_gentleman", "business_professional", "civic_official",
    "student", "young_local", "creative_local", "service_worker",
    "practical_worker", "commuter", "tech_worker",
    "university_researcher", "bike_commuter", "caregiver", "city_visitor",
    "dog_walker", "recreational_runner",
  ] as const satisfies readonly ResidentArchetypeId[]),
  university: Object.freeze([
    "student", "university_researcher", "tech_worker", "young_local",
    "business_professional",
  ] as const satisfies readonly ResidentArchetypeId[]),
  market: Object.freeze([
    "market_vendor", "old_town_craftsperson", "service_worker",
    "everyday_local", "senjore", "city_visitor",
  ] as const satisfies readonly ResidentArchetypeId[]),
  cafe: Object.freeze([
    "cafe_worker", "service_worker", "student", "tech_worker",
    "business_professional", "active_senior",
  ] as const satisfies readonly ResidentArchetypeId[]),
  park: Object.freeze([
    "park_worker", "dog_walker", "recreational_runner", "caregiver",
    "active_senior", "bike_commuter", "everyday_local",
  ] as const satisfies readonly ResidentArchetypeId[]),
  visitor_route: Object.freeze([
    "city_visitor", "tour_guide", "everyday_local", "business_professional",
    "senior_gentleman",
  ] as const satisfies readonly ResidentArchetypeId[]),
  worksite: Object.freeze([
    "restoration_worker", "practical_worker",
  ] as const satisfies readonly ResidentArchetypeId[]),
} as const);

export type ResidentArchetypeContext = keyof typeof RESIDENT_ARCHETYPE_CONTEXTS;

interface AppearancePalette {
  readonly outerwear: number;
  readonly lower: number;
  readonly accent: number;
}

interface ResidentArchetypeRecipe {
  readonly ambientWeight: number;
  readonly ageGroups: readonly CharacterAgeGroup[];
  readonly bodyShapes?: readonly BodyShape[];
  readonly statures?: readonly CharacterStature[];
  readonly hairStyles: readonly HairStyle[];
  readonly hairColours?: readonly HairColour[];
  readonly facialHair: readonly FacialHair[];
  readonly outerwear: readonly Outerwear[];
  readonly lowerGarments: readonly LowerGarment[];
  readonly footwear: readonly Footwear[];
  readonly eyewear: readonly Eyewear[];
  readonly headwear: readonly Headwear[];
  readonly neckwear: readonly Neckwear[];
  readonly carriedAccessories: readonly CarriedAccessory[];
  readonly palettes: readonly AppearancePalette[];
}

const QUIET_CITY_PALETTES = Object.freeze([
  { outerwear: 0x303235, lower: 0x263241, accent: 0x8b3c5a },
  { outerwear: 0x55585c, lower: 0x303235, accent: 0xb38c39 },
  { outerwear: 0x5a4338, lower: 0x383b3b, accent: 0x39747a },
  { outerwear: 0x3f5547, lower: 0x30363a, accent: 0xa45c3d },
  { outerwear: 0x6d6258, lower: 0x343a43, accent: 0x788f72 },
] as const);

const RESIDENT_ARCHETYPE_RECIPES: Readonly<
  Record<ResidentArchetypeId, ResidentArchetypeRecipe>
> = Object.freeze({
  everyday_local: {
    ambientWeight: 15,
    ageGroups: ["young_adult", "adult", "adult", "older_adult"],
    hairStyles: ["cropped", "parted", "bob", "wavy", "ponytail", "bald"],
    facialHair: ["none", "none", "none", "stubble", "moustache"],
    outerwear: [
      "wool_coat",
      "field_jacket",
      "cardigan",
      "raincoat",
      "quilted_coat",
      "denim_jacket",
    ],
    lowerGarments: ["trousers", "trousers", "wide_trousers", "skirt"],
    footwear: ["walking_boots", "trainers"],
    eyewear: ["none", "none", "round_glasses", "square_glasses"],
    headwear: ["none", "none", "knit_cap", "beret"],
    neckwear: ["none", "none", "scarf", "kerchief"],
    carriedAccessories: [
      "none",
      "none",
      "tote",
      "satchel",
      "shopping_bag",
      "backpack",
    ],
    palettes: QUIET_CITY_PALETTES,
  },
  sportswear_local: {
    ambientWeight: 7,
    ageGroups: ["young_adult", "young_adult", "adult"],
    hairStyles: ["buzz_cut", "cropped", "undercut", "ponytail"],
    facialHair: ["none", "none", "stubble", "short_beard"],
    outerwear: ["track_jacket"],
    lowerGarments: ["track_pants"],
    footwear: ["trainers"],
    eyewear: ["none", "none", "square_glasses"],
    headwear: ["none", "none", "baseball_cap", "knit_cap"],
    neckwear: ["none", "none", "chain"],
    carriedAccessories: ["none", "none", "backpack", "satchel"],
    palettes: [
      { outerwear: 0x263241, lower: 0x252a2e, accent: 0x6d879e },
      { outerwear: 0x324a3f, lower: 0x282d2b, accent: 0xa45c3d },
      { outerwear: 0x4d3039, lower: 0x2d2d32, accent: 0xb5a78f },
      { outerwear: 0x3d4148, lower: 0x26292d, accent: 0x788f72 },
    ],
  },
  senjore: {
    ambientWeight: 8,
    ageGroups: ["older_adult"],
    hairStyles: ["cropped", "bob", "bun", "wavy"],
    hairColours: ["silver", "silver", "ash", "auburn"],
    facialHair: ["none"],
    outerwear: ["quilted_coat", "wool_coat", "cardigan", "raincoat"],
    lowerGarments: ["skirt", "wide_trousers", "trousers"],
    footwear: ["walking_boots", "walking_boots", "dress_shoes"],
    eyewear: ["none", "round_glasses", "square_glasses"],
    headwear: ["none", "headscarf", "knit_cap", "beret"],
    neckwear: ["none", "scarf", "kerchief"],
    carriedAccessories: ["shopping_bag", "tote", "cane", "none"],
    palettes: [
      { outerwear: 0x69505f, lower: 0x3e3a43, accent: 0xb38a82 },
      { outerwear: 0x596653, lower: 0x3b4038, accent: 0xb38c39 },
      { outerwear: 0x6f5b49, lower: 0x3d3a37, accent: 0x39747a },
      { outerwear: 0x596b6d, lower: 0x39444d, accent: 0x9eb0aa },
    ],
  },
  active_senior: {
    ambientWeight: 4,
    ageGroups: ["older_adult"],
    hairStyles: ["cropped", "bob", "wavy", "parted", "bald"],
    hairColours: ["silver", "ash", "auburn"],
    facialHair: ["none", "none", "moustache"],
    outerwear: ["denim_jacket", "cardigan", "field_jacket", "hoodie"],
    lowerGarments: ["wide_trousers", "trousers", "track_pants"],
    footwear: ["trainers", "walking_boots"],
    eyewear: ["none", "round_glasses", "square_glasses"],
    headwear: ["none", "knit_cap", "baseball_cap"],
    neckwear: ["none", "scarf"],
    carriedAccessories: ["backpack", "tote", "satchel", "none"],
    palettes: [
      { outerwear: 0x39747a, lower: 0x343a43, accent: 0xb38c39 },
      { outerwear: 0x4a566f, lower: 0x303235, accent: 0xa45c3d },
      { outerwear: 0x77505f, lower: 0x3e3a43, accent: 0x788f72 },
      { outerwear: 0x3f624f, lower: 0x303235, accent: 0x47719b },
    ],
  },
  senior_gentleman: {
    ambientWeight: 5,
    ageGroups: ["older_adult"],
    hairStyles: ["bald", "cropped", "parted", "buzz_cut"],
    hairColours: ["silver", "silver", "ash"],
    facialHair: ["none", "moustache", "moustache", "short_beard"],
    outerwear: ["wool_coat", "field_jacket", "cardigan", "quilted_coat"],
    lowerGarments: ["trousers", "wide_trousers"],
    footwear: ["walking_boots", "dress_shoes"],
    eyewear: ["none", "round_glasses", "square_glasses"],
    headwear: ["none", "flat_cap", "beret", "knit_cap"],
    neckwear: ["none", "scarf", "tie"],
    carriedAccessories: ["none", "cane", "satchel", "shopping_bag"],
    palettes: QUIET_CITY_PALETTES,
  },
  business_professional: {
    ambientWeight: 8,
    ageGroups: ["young_adult", "adult", "adult", "older_adult"],
    hairStyles: ["cropped", "parted", "bob", "ponytail", "wavy"],
    facialHair: ["none", "none", "stubble", "short_beard"],
    outerwear: ["suit_jacket", "suit_jacket", "wool_coat", "raincoat"],
    lowerGarments: ["trousers", "trousers", "wide_trousers", "skirt"],
    footwear: ["dress_shoes", "dress_shoes", "walking_boots"],
    eyewear: ["none", "none", "square_glasses"],
    headwear: ["none", "none", "beret"],
    neckwear: ["none", "tie", "scarf"],
    carriedAccessories: ["briefcase", "satchel", "backpack", "none"],
    palettes: [
      { outerwear: 0x263241, lower: 0x303235, accent: 0xb5a78f },
      { outerwear: 0x55585c, lower: 0x303235, accent: 0x8b3c5a },
      { outerwear: 0x5a4338, lower: 0x343a43, accent: 0xd0c5af },
      { outerwear: 0x6d6258, lower: 0x303235, accent: 0x39747a },
    ],
  },
  civic_official: {
    ambientWeight: 4,
    ageGroups: ["young_adult", "adult", "adult", "older_adult"],
    hairStyles: ["cropped", "parted", "bob", "ponytail", "wavy"],
    facialHair: ["none", "none", "moustache", "short_beard"],
    outerwear: ["suit_jacket", "wool_coat"],
    lowerGarments: ["trousers", "trousers", "skirt"],
    footwear: ["dress_shoes"],
    eyewear: ["none", "square_glasses", "round_glasses"],
    headwear: ["none"],
    neckwear: ["tie", "tie", "scarf", "none"],
    carriedAccessories: ["briefcase", "satchel", "none"],
    palettes: [
      { outerwear: 0x293747, lower: 0x2d3137, accent: 0xb79354 },
      { outerwear: 0x494f59, lower: 0x2f3338, accent: 0xd0c5af },
      { outerwear: 0x344352, lower: 0x303235, accent: 0x788f72 },
    ],
  },
  student: {
    ambientWeight: 9,
    ageGroups: ["young_adult", "young_adult", "adult"],
    hairStyles: ["buzz_cut", "cropped", "undercut", "wavy", "ponytail", "bob"],
    facialHair: ["none", "none", "stubble", "short_beard"],
    outerwear: ["hoodie", "denim_jacket", "field_jacket", "quilted_coat"],
    lowerGarments: ["wide_trousers", "trousers", "track_pants", "skirt"],
    footwear: ["trainers", "walking_boots"],
    eyewear: ["none", "none", "round_glasses"],
    headwear: ["none", "none", "knit_cap", "baseball_cap"],
    neckwear: ["none", "scarf"],
    carriedAccessories: ["backpack", "backpack", "tote", "satchel"],
    palettes: [
      { outerwear: 0x4f3a45, lower: 0x263241, accent: 0x47719b },
      { outerwear: 0x3f5547, lower: 0x303235, accent: 0xb38c39 },
      { outerwear: 0x43566b, lower: 0x343a43, accent: 0xd0c5af },
      { outerwear: 0x55585c, lower: 0x3e3a43, accent: 0x8b3c5a },
    ],
  },
  young_local: {
    ambientWeight: 6,
    ageGroups: ["young_adult"],
    hairStyles: ["buzz_cut", "undercut", "cropped", "wavy", "ponytail", "bob"],
    facialHair: ["none", "none", "stubble", "short_beard"],
    outerwear: ["hoodie", "denim_jacket", "track_jacket", "raincoat"],
    lowerGarments: ["wide_trousers", "track_pants", "trousers", "skirt"],
    footwear: ["trainers", "walking_boots"],
    eyewear: ["none", "none", "round_glasses", "square_glasses"],
    headwear: ["none", "knit_cap", "baseball_cap"],
    neckwear: ["none", "chain", "scarf"],
    carriedAccessories: ["backpack", "tote", "satchel", "none"],
    palettes: QUIET_CITY_PALETTES,
  },
  creative_local: {
    ambientWeight: 7,
    ageGroups: ["young_adult", "adult", "older_adult"],
    hairStyles: ["wavy", "bob", "ponytail", "bun", "undercut", "cropped"],
    facialHair: ["none", "none", "moustache", "short_beard"],
    outerwear: ["denim_jacket", "cardigan", "raincoat", "hoodie"],
    lowerGarments: ["wide_trousers", "skirt", "trousers"],
    footwear: ["walking_boots", "trainers", "dress_shoes"],
    eyewear: ["none", "round_glasses", "round_glasses", "square_glasses"],
    headwear: ["none", "beret", "knit_cap"],
    neckwear: ["none", "scarf", "chain"],
    carriedAccessories: ["tote", "satchel", "backpack", "none"],
    palettes: [
      { outerwear: 0x4d5550, lower: 0x303235, accent: 0xb38c39 },
      { outerwear: 0x5a4338, lower: 0x343a43, accent: 0x39747a },
      { outerwear: 0x5d4b61, lower: 0x3e3a43, accent: 0xa45c3d },
      { outerwear: 0x435b62, lower: 0x303235, accent: 0x8b3c5a },
    ],
  },
  service_worker: {
    ambientWeight: 6,
    ageGroups: ["young_adult", "adult", "older_adult"],
    hairStyles: ["cropped", "bob", "ponytail", "buzz_cut", "wavy"],
    facialHair: ["none", "none", "stubble"],
    outerwear: ["field_jacket", "cardigan", "denim_jacket"],
    lowerGarments: ["trousers", "wide_trousers"],
    footwear: ["walking_boots", "trainers", "work_boots"],
    eyewear: ["none", "none", "square_glasses"],
    headwear: ["none", "knit_cap", "baseball_cap"],
    neckwear: ["none", "kerchief"],
    carriedAccessories: ["none", "none", "satchel", "shopping_bag"],
    palettes: [
      { outerwear: 0x39433f, lower: 0x303235, accent: 0xb77a45 },
      { outerwear: 0x4a4540, lower: 0x2f3435, accent: 0xd0c5af },
      { outerwear: 0x435158, lower: 0x303235, accent: 0x788f72 },
    ],
  },
  practical_worker: {
    ambientWeight: 7,
    ageGroups: ["young_adult", "adult", "adult", "older_adult"],
    hairStyles: ["buzz_cut", "cropped", "parted", "ponytail", "bald"],
    facialHair: ["none", "stubble", "moustache", "short_beard"],
    outerwear: ["field_jacket", "denim_jacket", "quilted_coat"],
    lowerGarments: ["trousers", "trousers", "wide_trousers"],
    footwear: ["work_boots", "walking_boots"],
    eyewear: ["none", "none", "square_glasses"],
    headwear: ["none", "knit_cap", "baseball_cap", "flat_cap"],
    neckwear: ["none", "scarf"],
    carriedAccessories: ["none", "satchel", "backpack", "shopping_bag"],
    palettes: [
      { outerwear: 0x3f5547, lower: 0x303235, accent: 0xb38c39 },
      { outerwear: 0x4a5154, lower: 0x2d3134, accent: 0xa45c3d },
      { outerwear: 0x5a4338, lower: 0x353a38, accent: 0x788f72 },
    ],
  },
  commuter: {
    ambientWeight: 14,
    ageGroups: ["young_adult", "adult", "adult", "older_adult"],
    hairStyles: ["cropped", "parted", "bob", "wavy", "ponytail", "bald"],
    facialHair: ["none", "none", "stubble", "moustache"],
    outerwear: ["raincoat", "quilted_coat", "wool_coat", "denim_jacket"],
    lowerGarments: ["trousers", "wide_trousers", "skirt"],
    footwear: ["walking_boots", "trainers", "dress_shoes"],
    eyewear: ["none", "none", "round_glasses", "square_glasses"],
    headwear: ["none", "none", "knit_cap", "beret"],
    neckwear: ["none", "scarf", "kerchief"],
    carriedAccessories: ["backpack", "tote", "satchel", "briefcase", "none"],
    palettes: QUIET_CITY_PALETTES,
  },
  tech_worker: {
    ambientWeight: 5,
    ageGroups: ["young_adult", "adult", "adult", "older_adult"],
    hairStyles: ["cropped", "parted", "bob", "wavy", "ponytail", "bald"],
    facialHair: ["none", "none", "stubble", "short_beard"],
    outerwear: ["hoodie", "field_jacket", "wool_coat", "raincoat"],
    lowerGarments: ["trousers", "wide_trousers"],
    footwear: ["trainers", "walking_boots"],
    eyewear: ["none", "none", "round_glasses", "square_glasses"],
    headwear: ["none", "none", "knit_cap"],
    neckwear: ["none", "scarf"],
    carriedAccessories: ["backpack", "folio", "satchel", "none"],
    palettes: QUIET_CITY_PALETTES,
  },
  university_researcher: {
    ambientWeight: 3,
    ageGroups: ["young_adult", "adult", "adult", "older_adult"],
    hairStyles: ["cropped", "parted", "bob", "wavy", "ponytail", "bun", "bald"],
    facialHair: ["none", "none", "moustache", "short_beard"],
    outerwear: ["cardigan", "raincoat", "wool_coat"],
    lowerGarments: ["trousers", "wide_trousers", "skirt"],
    footwear: ["walking_boots", "dress_shoes"],
    eyewear: ["none", "round_glasses", "square_glasses"],
    headwear: ["none", "none", "beret"],
    neckwear: ["none", "scarf", "kerchief"],
    carriedAccessories: ["folio", "satchel", "tote"],
    palettes: QUIET_CITY_PALETTES,
  },
  bike_commuter: {
    ambientWeight: 4,
    ageGroups: ["young_adult", "adult", "adult", "older_adult"],
    hairStyles: ["buzz_cut", "cropped", "undercut", "bob", "ponytail"],
    facialHair: ["none", "none", "stubble", "short_beard"],
    outerwear: ["raincoat", "field_jacket", "track_jacket"],
    lowerGarments: ["trousers", "track_pants"],
    footwear: ["trainers", "walking_boots"],
    eyewear: ["none", "none", "square_glasses"],
    headwear: ["cycle_helmet"],
    neckwear: ["none", "scarf"],
    carriedAccessories: ["backpack", "backpack", "none"],
    palettes: QUIET_CITY_PALETTES,
  },
  cafe_worker: {
    ambientWeight: 0,
    ageGroups: ["young_adult", "adult", "older_adult"],
    hairStyles: ["cropped", "bob", "ponytail", "bun", "buzz_cut"],
    facialHair: ["none", "none", "stubble", "short_beard"],
    outerwear: ["cardigan", "denim_jacket", "field_jacket"],
    lowerGarments: ["trousers", "wide_trousers"],
    footwear: ["trainers", "walking_boots"],
    eyewear: ["none", "none", "round_glasses"],
    headwear: ["none"],
    neckwear: ["kerchief", "none"],
    carriedAccessories: ["none", "tool_roll"],
    palettes: QUIET_CITY_PALETTES,
  },
  market_vendor: {
    ambientWeight: 0,
    ageGroups: ["young_adult", "adult", "adult", "older_adult"],
    hairStyles: ["cropped", "parted", "bob", "wavy", "bun", "bald"],
    facialHair: ["none", "none", "moustache", "short_beard"],
    outerwear: ["quilted_coat", "cardigan", "field_jacket"],
    lowerGarments: ["trousers", "wide_trousers", "skirt"],
    footwear: ["work_boots", "walking_boots"],
    eyewear: ["none", "round_glasses", "square_glasses"],
    headwear: ["none", "knit_cap", "flat_cap"],
    neckwear: ["none", "kerchief", "scarf"],
    carriedAccessories: ["produce_basket", "shopping_bag", "none"],
    palettes: QUIET_CITY_PALETTES,
  },
  health_worker: {
    ambientWeight: 0,
    ageGroups: ["young_adult", "adult", "adult", "older_adult"],
    hairStyles: ["cropped", "parted", "bob", "ponytail", "bun", "bald"],
    facialHair: ["none", "none", "stubble", "short_beard"],
    outerwear: ["raincoat", "wool_coat", "cardigan"],
    lowerGarments: ["trousers", "wide_trousers"],
    footwear: ["trainers", "walking_boots"],
    eyewear: ["none", "none", "square_glasses"],
    headwear: ["none"],
    neckwear: ["lanyard"],
    carriedAccessories: ["tote", "folio", "backpack"],
    palettes: QUIET_CITY_PALETTES,
  },
  park_worker: {
    ambientWeight: 0,
    ageGroups: ["young_adult", "adult", "adult", "older_adult"],
    hairStyles: ["buzz_cut", "cropped", "parted", "ponytail", "bald"],
    facialHair: ["none", "stubble", "moustache", "short_beard"],
    outerwear: ["field_jacket", "quilted_coat"],
    lowerGarments: ["trousers", "wide_trousers"],
    footwear: ["work_boots"],
    eyewear: ["none", "none", "square_glasses"],
    headwear: ["baseball_cap", "knit_cap", "none"],
    neckwear: ["none", "scarf"],
    carriedAccessories: ["tool_roll", "none"],
    palettes: QUIET_CITY_PALETTES,
  },
  caregiver: {
    ambientWeight: 3,
    ageGroups: ["young_adult", "adult", "adult", "older_adult"],
    hairStyles: ["cropped", "parted", "bob", "wavy", "ponytail", "bun", "bald"],
    facialHair: ["none", "none", "stubble", "short_beard"],
    outerwear: ["raincoat", "quilted_coat", "field_jacket"],
    lowerGarments: ["trousers", "wide_trousers", "skirt"],
    footwear: ["walking_boots", "trainers"],
    eyewear: ["none", "none", "round_glasses"],
    headwear: ["none", "knit_cap"],
    neckwear: ["none", "scarf"],
    carriedAccessories: ["stroller"],
    palettes: QUIET_CITY_PALETTES,
  },
  city_visitor: {
    ambientWeight: 3,
    ageGroups: ["young_adult", "adult", "adult", "older_adult"],
    hairStyles: ["cropped", "parted", "bob", "wavy", "ponytail", "bald"],
    facialHair: ["none", "none", "stubble", "short_beard"],
    outerwear: ["raincoat", "quilted_coat", "wool_coat"],
    lowerGarments: ["trousers", "wide_trousers", "skirt"],
    footwear: ["walking_boots", "trainers"],
    eyewear: ["none", "round_glasses", "square_glasses"],
    headwear: ["none", "knit_cap", "baseball_cap"],
    neckwear: ["none", "scarf"],
    carriedAccessories: ["camera", "backpack", "satchel"],
    palettes: QUIET_CITY_PALETTES,
  },
  tour_guide: {
    ambientWeight: 0,
    ageGroups: ["young_adult", "adult", "adult", "older_adult"],
    hairStyles: ["cropped", "parted", "bob", "wavy", "ponytail", "bald"],
    facialHair: ["none", "none", "moustache", "short_beard"],
    outerwear: ["wool_coat", "raincoat", "field_jacket"],
    lowerGarments: ["trousers", "wide_trousers", "skirt"],
    footwear: ["walking_boots", "dress_shoes"],
    eyewear: ["none", "round_glasses", "square_glasses"],
    headwear: ["none", "beret"],
    neckwear: ["scarf", "kerchief"],
    carriedAccessories: ["folio", "satchel", "camera"],
    palettes: QUIET_CITY_PALETTES,
  },
  old_town_craftsperson: {
    ambientWeight: 0,
    ageGroups: ["young_adult", "adult", "adult", "older_adult"],
    hairStyles: ["cropped", "parted", "bob", "wavy", "ponytail", "bun", "bald"],
    facialHair: ["none", "none", "moustache", "short_beard"],
    outerwear: ["cardigan", "field_jacket", "denim_jacket"],
    lowerGarments: ["trousers", "wide_trousers"],
    footwear: ["work_boots", "walking_boots"],
    eyewear: ["none", "round_glasses", "square_glasses"],
    headwear: ["none", "flat_cap", "beret"],
    neckwear: ["none", "kerchief"],
    carriedAccessories: ["tool_roll", "tote", "satchel"],
    palettes: QUIET_CITY_PALETTES,
  },
  restoration_worker: {
    ambientWeight: 0,
    ageGroups: ["young_adult", "adult", "adult", "older_adult"],
    hairStyles: ["buzz_cut", "cropped", "parted", "ponytail", "bald"],
    facialHair: ["none", "stubble", "moustache", "short_beard"],
    outerwear: ["field_jacket", "quilted_coat"],
    lowerGarments: ["trousers", "wide_trousers"],
    footwear: ["work_boots"],
    eyewear: ["none", "square_glasses"],
    headwear: ["hard_hat"],
    neckwear: ["none", "scarf"],
    carriedAccessories: ["tool_roll"],
    palettes: QUIET_CITY_PALETTES,
  },
  dog_walker: {
    ambientWeight: 2,
    ageGroups: ["young_adult", "adult", "adult", "older_adult"],
    hairStyles: ["cropped", "parted", "bob", "wavy", "ponytail", "bald"],
    facialHair: ["none", "none", "stubble", "moustache"],
    outerwear: ["field_jacket", "raincoat", "quilted_coat", "cardigan"],
    lowerGarments: ["trousers", "wide_trousers", "track_pants"],
    footwear: ["walking_boots", "trainers"],
    eyewear: ["none", "none", "round_glasses"],
    headwear: ["none", "knit_cap", "baseball_cap"],
    neckwear: ["none", "scarf"],
    carriedAccessories: ["dog_lead"],
    palettes: QUIET_CITY_PALETTES,
  },
  recreational_runner: {
    ambientWeight: 3,
    ageGroups: ["young_adult", "adult", "adult", "older_adult"],
    hairStyles: ["buzz_cut", "cropped", "undercut", "ponytail", "bob", "bald"],
    facialHair: ["none", "none", "stubble", "short_beard"],
    outerwear: ["hoodie", "track_jacket"],
    lowerGarments: ["track_pants"],
    footwear: ["trainers"],
    eyewear: ["none", "none", "square_glasses"],
    headwear: ["none", "baseball_cap", "knit_cap"],
    neckwear: ["none"],
    carriedAccessories: ["waist_pack"],
    palettes: QUIET_CITY_PALETTES,
  },
});

function stableHash(value: string): number {
  let hash = 2_166_136_261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return hash >>> 0;
}

function pick<T>(values: readonly T[], seed: string): T {
  const value = values[stableHash(seed) % values.length];
  if (value === undefined) {
    throw new Error("Resident appearance recipe option list must not be empty");
  }
  return value;
}

export function pickResidentArchetype(
  stableId: string,
  context: ResidentArchetypeContext = "citywide",
): ResidentArchetypeId {
  if (stableId.length === 0) {
    throw new Error("Resident archetype requires a stable ID");
  }
  const candidates = new Set<ResidentArchetypeId>(
    RESIDENT_ARCHETYPE_CONTEXTS[context],
  );
  if (context !== "citywide") {
    return pick(RESIDENT_ARCHETYPE_CONTEXTS[context], stableId + ":context");
  }
  const weighted = RESIDENT_ARCHETYPE_OPTIONS.flatMap(({ id }) =>
    !candidates.has(id)
      ? []
      :
    Array.from(
      { length: RESIDENT_ARCHETYPE_RECIPES[id].ambientWeight },
      () => id,
    )
  );
  if (weighted.length === 0) {
    return pick(RESIDENT_ARCHETYPE_CONTEXTS[context], stableId + ":context");
  }
  return pick(weighted, "resident-appearance-v2:" + stableId + ":archetype");
}

export interface ResidentAppearanceProfile {
  readonly archetype: ResidentArchetypeId;
  readonly appearance: CharacterAppearance;
}

export interface ResidentAppearanceOptions {
  readonly archetype?: ResidentArchetypeId;
  readonly context?: ResidentArchetypeContext;
  readonly overrides?: Readonly<CharacterAppearanceOverrides>;
}

export function generateResidentAppearanceProfile(
  stableId: string,
  options: Readonly<ResidentAppearanceOptions> = {},
): ResidentAppearanceProfile {
  const archetype = options.archetype ?? pickResidentArchetype(
    stableId,
    options.context,
  );
  const recipe = RESIDENT_ARCHETYPE_RECIPES[archetype];
  const seed = "resident-appearance-v2:" + stableId + ":" + archetype;
  const palette = pick(recipe.palettes, seed + ":palette");
  const recipeOverrides: CharacterAppearanceOverrides = {
    ageGroup: pick(recipe.ageGroups, seed + ":age"),
    hairStyle: pick(recipe.hairStyles, seed + ":hair-style"),
    facialHair: pick(recipe.facialHair, seed + ":facial-hair"),
    outerwear: pick(recipe.outerwear, seed + ":outerwear"),
    lowerGarment: pick(recipe.lowerGarments, seed + ":lower"),
    footwear: pick(recipe.footwear, seed + ":footwear"),
    eyewear: pick(recipe.eyewear, seed + ":eyewear"),
    headwear: pick(recipe.headwear, seed + ":headwear"),
    neckwear: pick(recipe.neckwear, seed + ":neckwear"),
    carriedAccessory: pick(recipe.carriedAccessories, seed + ":carried"),
    colours: palette,
    ...(recipe.bodyShapes === undefined
      ? {}
      : { bodyShape: pick(recipe.bodyShapes, seed + ":body") }),
    ...(recipe.statures === undefined
      ? {}
      : { stature: pick(recipe.statures, seed + ":stature") }),
    ...(recipe.hairColours === undefined
      ? {}
      : { hairColour: pick(recipe.hairColours, seed + ":hair-colour") }),
  };
  const overrides = options.overrides ?? {};
  return Object.freeze({
    archetype,
    appearance: generateCharacterAppearance(stableId, {
      ...recipeOverrides,
      ...overrides,
      colours: {
        ...recipeOverrides.colours,
        ...overrides.colours,
      },
    }),
  });
}

export function generateResidentAppearance(
  stableId: string,
  options: Readonly<ResidentAppearanceOptions> = {},
): CharacterAppearance {
  return generateResidentAppearanceProfile(stableId, options).appearance;
}
